"use strict";
// A maintenance contract's payment type (#515 slice 3; the template's Cost PA sheet and the Yearly
// Summaries' Maintenance Type, O6): how much of the contract's sell falls in each year, and the finance
// interest. Every type starts from the PAYG year sells P (each year's cost grossed up at the contract's
// GP %, U = their total, N years), and a category's contract sell (its share of U). Pure.
//
//   PAYG             each year pays its own work: share P_y / U, no interest.
//   Even Spread      each year pays U / N (rows 70–77); interest (W55, signed) = rate × Σ_k (ΣP_1..k − k × U / N),
//                    paid as interest / N every year (Yearly Summaries row 15: AT65 / years). CORRECTED (DD,
//                    2026-10-23): from year 9 the template takes interest × P_y / U instead (V15:AB15 → AM65:AP65)
//                    and so collects less than it works out; the app collects it all.
//   Higher Year One  ("Payment up front" in the template, rows 57–60, 78–85): year 1 pays the repaint's cost
//                    (CORRECTED, DD 2026-10-23: the real repaint cost, not Summary!M111, which differs with hire;
//                    and never more than year 1's own work, PR 547 review: a repaint moved past year 1 isn't paid
//                    for up front, so no later year's payment goes to nothing or below),
//                    later years the rest evenly; interest (AP60) = rate × Σ_k (ΣP_1..k − Σpaid_1..k)
//                    is added evenly to the later years. QUIRK: the Yearly Summaries share each category by the
//                    year's payment / the total payments (F83), so its year 1 is not the payment table's.
//
// CORRECTED (DD, 23 Oct 2026: a financed year with no work still carries its payment, on prelims): every year of the
// term pays its share, a year before any work has been done included. The template pays nothing in such a year
// (F74, F82), so with No Work years at the start it collects less than the contract; the app collects all of it.
Object.defineProperty(exports, "__esModule", { value: true });
exports.FINANCE_RATE_SETTING = exports.PAYMENT_TYPES = void 0;
exports.paymentSchedule = paymentSchedule;
/** CRM's opportunity.hig_maintenancetype labels (910500000 PAYG, …01 Even Spread, …02 Higher Year One). */
exports.PAYMENT_TYPES = ['PAYG', 'Even Spread', 'Higher Year One'];
exports.FINANCE_RATE_SETTING = 'maintenance.financerate';
const sum = (values) => values.reduce((t, v) => t + v, 0);
const cumulative = (values) => values.map((_, i) => sum(values.slice(0, i + 1)));
/**
 * The schedule for `type`. `paygSells`: each year's PAYG sell (P); `repaintCost`: Higher Year One's first payment,
 * the repaint's cost as far as year 1's work goes (yearlySummary); `financeRate`: a fraction (0.08). The first payment
 * is held within year 1's PAYG sell, so no later year goes negative.
 */
function paymentSchedule(type, paygSells, repaintCost, financeRate) {
    const years = paygSells.length;
    const total = sum(paygSells);
    const paid = cumulative(paygSells);
    const none = paygSells.map(() => 0);
    if (type === 'PAYG' || total <= 0 || years === 0) {
        return { type, shares: paygSells.map((p) => (total > 0 ? p / total : 0)), interest: none };
    }
    if (type === 'Even Spread') {
        const interest = financeRate * sum(paid.map((p, i) => p - ((i + 1) * total) / years));
        return { type, shares: paygSells.map(() => 1 / years), interest: paygSells.map(() => interest / years) };
    }
    // Higher Year One: the repaint's cost, then the rest over the service years, with the interest spread over them.
    const serviceYears = Math.max(1, years - 1);
    // Never ahead of year 1's own PAYG sell: paying ahead makes the interest negative, which could take a later year below nothing.
    const first = Math.min(Math.max(0, repaintCost), paygSells[0] ?? 0);
    const base = paygSells.map((_, i) => (i === 0 ? first : (total - first) / serviceYears));
    const interest = financeRate * sum(paid.map((p, i) => p - sum(base.slice(0, i + 1))));
    const payments = base.map((b, i) => (i === 0 ? b : b + interest / serviceYears));
    const allPayments = total + interest;
    return {
        type,
        shares: payments.map((p) => p / allPayments),
        interest: payments.map((_, i) => (i === 0 ? 0 : interest / serviceYears)),
    };
}
